import { Head } from "zudoku/components";
import { useLocation } from "zudoku/router";
import { SITE, BRAND_IMAGE, FEED_PATH, seoTitle } from "./brand";
import { authorFor, authorEntityId, authorUrl } from "../config/authors";
import blogPosts from "../config/blogPosts";

/**
 * SeoHead — the per-post social/structured-data tags Zudoku has no config for.
 *
 * Mounted on the `layout-after-head` slot (zudoku.config.tsx), so it renders on
 * every page that goes through Zudoku's Layout. It emits tags for BLOG POSTS
 * ONLY and no-ops everywhere else; see "Scope" below.
 *
 * ── What this does NOT do, on purpose ──────────────────────────────────────
 * It does not touch <meta name=description> or <link rel=canonical>. Zudoku
 * already emits both per page and does it correctly:
 *
 *   description  MdxPage pushes frontmatter.description.
 *   canonical    Meta.tsx builds it from canonicalUrlOrigin + basePath + path.
 *
 * Pushing our own copies here would put two writers on the same unhead keys and
 * turn a config question into a tag-precedence question. The split is: Zudoku
 * owns the head tags it knows about, this file owns the ones it does not
 * (og:*, twitter:*, and the Article JSON-LD).
 *
 * ── The one exception: <title> ─────────────────────────────────────────────
 * <title> IS overridden here, and it is the deliberate exception to the rule
 * above, because zudoku offers no way to make the change from config.
 *
 * MdxPage renders `<title>{frontmatter.title}</title>` and ALSO renders that
 * same string as the page's visible <h1> (plugins/markdown/MdxPage.tsx). One
 * value, two jobs — so the fix cannot be made at the source. Shortening the
 * frontmatter title in sync_content.py, or in config/blogPosts.json, would put
 * "Recruiting..." at the top of the article body as well.
 *
 * So this pushes a second, shorter title for the SAME unhead key. Being a
 * second writer is the price of separating the two jobs, and it is won on
 * tagPriority rather than on render order — see the tag itself. The outcome is
 * asserted against the built HTML on BOTH deploy paths (deploy.sh step 4 and
 * the "Verify the build" step in .github/workflows/deploy-external-*.yml), so
 * losing that dedupe fails the deploy instead of quietly un-fixing 104 pages.
 *
 * Note what is NOT truncated: og:title, twitter:title, the JSON-LD headline and
 * the Atom feed all keep post.title in full below. None of them is a SERP title
 * and none has a 70-character budget. See seoTitle() in brand.ts.
 *
 * ── Where the data comes from ──────────────────────────────────────────────
 * config/blogPosts.json, which sync_content.py regenerates from the markdown
 * frontmatter on every deploy. It is already in the client bundle (BlogHome
 * imports it), so the lookup adds no payload. A post added to the S3 bucket is
 * fully tagged on the next ./deploy.sh with no code change here — the same
 * property scripts/generate-sitemap.mjs relies on.
 *
 * ── Scope: blog posts only ─────────────────────────────────────────────────
 * Docs pages (/docs/*) and the API playground get nothing from this component.
 * blogPosts.json is the only registry carrying a description and an image per
 * page; docsNav.json has labels only, and og:image with no image is worse than
 * no og:image. They still get title, description and canonical from Zudoku.
 *
 * The blog index (/) is a `custom-page` with layout:"none", which bypasses
 * Layout and therefore this slot entirely. Its tags live in BlogHome.tsx.
 */

/**
 * unhead reads `tagPriority` out of a tag's props (TagConfigKeys, in
 * unhead/dist/shared) — but React's JSX types for <title> know nothing about
 * it, so it is spread in through a typed alias rather than written inline.
 */
const TITLE_WINS = { tagPriority: "high" } as unknown as { key?: string };

/** Absolute URL for an app-root-relative path. */
const abs = (path: string) => `${SITE}${path.startsWith("/") ? path : `/${path}`}`;

export default function SeoHead() {
  const { pathname } = useLocation();

  // Inside the app, pathname is basePath-relative ("/blogs/foo", not
  // "/pub/blogs/foo") — react-router strips the basename. The /pub prefix is
  // added back below for the absolute URLs, and it is ALREADY PRESENT on
  // post.image, which sync_content.py writes as "/pub/imgs/…". Prefixing that
  // again is the /pub/pub/ trap zudoku.config.tsx's logo comment warns about.
  const match = /^\/blogs\/([^/]+)\/?$/.exec(pathname);
  if (!match) return null;

  const post = blogPosts.find((p) => p.slug === match[1]);
  if (!post) return null;

  const url = abs(`/pub/blogs/${post.slug}`);
  const image = abs(post.image);

  // NOTE: `lastModified` is the frontmatter's `lastModifiedTime`, and it is the
  // only date the content carries — there is no separate publish date to draw
  // on. It is emitted as dateModified only; claiming it as datePublished would
  // assert something the source does not say. Adding a real `date:` frontmatter
  // field and threading it through sync_content.py is the follow-up that makes
  // datePublished honest.
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    image: [image],
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    ...(post.lastModified ? { dateModified: post.lastModified } : {}),
    // A PERSON, not the Organization. The @id is the one the marketing site's
    // /about/<slug> page declares for the same human, so every post references a
    // single entity in the graph rather than restating a new one per post — and
    // that entity has a job title, an employer and a sameAs LinkedIn profile
    // attached to it there. `name` and `url` are included so the node still
    // stands alone for a consumer that does not resolve the @id.
    author: (() => {
      const a = authorFor((post as { author?: string }).author);
      return { "@type": "Person", "@id": authorEntityId(a), name: a.name, url: authorUrl(a) };
    })(),
    publisher: {
      "@type": "Organization",
      name: "HRMLESS",
      url: SITE,
      // Raster, not the .svg the site header uses: Google's Article rich-result
      // validator rejects SVG for Organization.logo.
      logo: { "@type": "ImageObject", url: abs(BRAND_IMAGE) },
    },
  };

  return (
    <Head>
      {/* Feed autodiscovery — the mechanism readers actually use to find
          /pub/feed.xml, which is why generate-feed.mjs writes no rss.xml. */}
      <link
        rel="alternate"
        type="application/atom+xml"
        title="The HRMLESS Blog"
        href={`${SITE}${FEED_PATH}`}
      />
      {/* SERP-length title — see the "one exception" note above. Only the 104
          posts whose rendered title exceeds TITLE_MAX are shortened; the rest
          come through seoTitle() unchanged. The "%s | HRMLESS" suffix is still
          appended by zudoku's titleTemplate after this value, and seoTitle has
          already left room for it.

          tagPriority is NOT decoration. <title> is a unique tag in unhead, so
          this one and MdxPage's are the same dedupe key and exactly one
          survives. Ties are broken by registration order, and the
          `layout-after-head` slot registers BEFORE the route's own content —
          without a priority MdxPage wins and this whole component is a no-op
          that still renders a perfectly good page. Verified by building it that
          way first. "high" resolves to weight 99 against the default 100
          (TagPriorityAliases in unhead), which settles it on weight rather than
          on render order. */}
      <title {...TITLE_WINS}>{seoTitle(post.title)}</title>
      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="HRMLESS" />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={post.description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={post.title} />
      {post.lastModified && (
        <meta property="article:modified_time" content={post.lastModified} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={post.title} />
      <meta name="twitter:description" content={post.description} />
      <meta name="twitter:image" content={image} />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
    </Head>
  );
}
